import React from "react";
import { VACANCY_CANDIDATE_VISIBILITY, VACANCY_SELECTION_METHOD } from "../../../../models/vacancy";
import {
  getJobCentreNameByVacancy,
  getJobTitleNameForCandidate,
} from "../../../../services/candidateJobsService";
import { TJobData } from "../../../../store/candidate/types";
import CustomButton from "../../../UI/Buttons/CustomButton";
import RoundedTick from "../../../UI/Icons/RoundedTick";
import Checkbox from "../../../UI/Inputs/Checkbox";
import PopupModal from "../../../UI/Popup";
import { formatTimeframes } from "../../../Vacancy/timeframeApiHelper";
import { getCandidateLabel } from "../CandidateVacancyCard";

interface ApplyJobModalProps {
  showApplyJobModal: boolean;
  setShowApplyJobModal: React.Dispatch<React.SetStateAction<boolean>>;
  onClickClose?: () => void;
  vacancyData: TJobData;
  handleApplyForJob: (vacancyData: TJobData) => void;
}

const AcceptJobModal: React.FC<ApplyJobModalProps> = ({
  showApplyJobModal,
  handleApplyForJob,
  onClickClose,
  setShowApplyJobModal,
  vacancyData,
}) => {
  const isELVCentre = vacancyData?.is_elv_centre;
  const candidatePastJobsCount = vacancyData?.candidate_past_jobs_count || 0;
  const [acceptTerms, setAcceptTerms] = React.useState({
    staff_code: false,
    child_safety_code: false,
    elv_staff_induction: false
  });

  const handleClose = () => {
    setShowApplyJobModal(false);
    setAcceptTerms({
      staff_code: false,
      child_safety_code: false,
      elv_staff_induction: false
    });
    if (onClickClose) {
      onClickClose();
    }
  };

  const handleChange = (id: string) => (checked: boolean) => {
    setAcceptTerms(prev => ({
      ...prev,
      [id]: checked
    }));
  };

  const allTermsAccepted = acceptTerms.staff_code && acceptTerms.child_safety_code && acceptTerms.elv_staff_induction;

  React.useEffect(() => {
    if (showApplyJobModal) {
      setAcceptTerms({
        staff_code: false,
        child_safety_code: false,
        elv_staff_induction: false
      });
    }
  }, [showApplyJobModal]);

  const jobType = getCandidateLabel({
        selectionMethod: vacancyData.selection_method as VACANCY_SELECTION_METHOD,
        visibilityType: vacancyData.visibility_type as VACANCY_CANDIDATE_VISIBILITY,
        visibilityUpgraded: vacancyData.visibility_upgraded,
      })


  return (
    <PopupModal
      visible={showApplyJobModal}
      showCloseBtn={true}
      onClickClose={handleClose}
      customWidth={650}
    >
      <div className="gap-20 fs-22">{jobType === "Accept" ? "Accept" : "Apply for"} this job</div>
      <div className="fs-13">
        {
          isELVCentre && candidatePastJobsCount === 0 ? (
            <>Please confirm you agree to the below terms, in order to {jobType === "Accept" ? "accept" : "apply for"} this casual job</>
          ) : (            
            <>Please confirm you would like to accept this job</>
          )
        }:{" "}
        {getJobTitleNameForCandidate(vacancyData)} at{" "}
        {getJobCentreNameByVacancy(vacancyData)}, {vacancyData?.centre?.address}
        {vacancyData?.timeframes &&
          formatTimeframes(vacancyData?.timeframes)?.map(
            (formatedTimeData, index) => {
              const label = formatedTimeData.label;
              const isToday = label.toLowerCase().startsWith("today");
              const isTomorrow = label.toLowerCase().startsWith("tomorrow");
              const showOn = !isToday && !isTomorrow;
              const displayLabel = (isToday || isTomorrow) 
                ? label.charAt(0).toLowerCase() + label.slice(1)
                : label;

              return (
                <span
                  key={index}
                  className={`fs-13 ${
                    formatedTimeData.alert ? "text-alert fw-700" : "fw-400"
                  }`}
                >
                  {showOn ? " on " : " "}{displayLabel}
                </span>
              );
            }
          )}.
      </div>


      {isELVCentre && candidatePastJobsCount === 0 && (
        <>
          <div className="fw-bold fs-13px">View and agree to these policies:</div>
            <div className="d-flex flex-column gap-10">
                <div className="theme-check">
                    <Checkbox
                        className="theme-check-input fs-16 fw-400"
                        id="staff_code"
                        checked={acceptTerms.staff_code}
                        onChange={handleChange("staff_code")}
                        label={"I have read and agree to the Staff Code of Conduct ELV Policy, Child Safety Code of Conduct ELV Policy, and ELV Staff Induction"}
                        themeCheck={true}
                        // disabled={checkStageSubmittedOrNot(3)}
                    />
                </div>
                <div className="theme-check">
                    <Checkbox
                        className="theme-check-input fs-16 fw-400"
                        id="child_safety_code"
                        checked={acceptTerms.child_safety_code}
                        onChange={handleChange("child_safety_code")}
                        label={"I have read and agree to the Staff Code of Conduct ELV Policy, Child Safety Code of Conduct ELV Policy, and ELV Staff Induction"}
                        themeCheck={true}
                        // disabled={checkStageSubmittedOrNot(3)}
                    />
                </div>
                <div className="theme-check">
                    <Checkbox
                        className="theme-check-input fs-16 fw-400"
                        id="elv_staff_induction"
                        checked={acceptTerms.elv_staff_induction}
                        onChange={handleChange("elv_staff_induction")}
                        label={"I have read and agree to the Staff Code of Conduct ELV Policy, Child Safety Code of Conduct ELV Policy, and ELV Staff Induction"}
                        themeCheck={true}
                        // disabled={checkStageSubmittedOrNot(3)}
                    />
                </div>
          </div>
        </>
      )}
      
      <div className="d-flex gap-20 justify-content-end">
        <CustomButton
          variant={"primary"}
          type="button"
          className="btn-theme bg-teal text-black"
          onClick={() => handleApplyForJob(vacancyData)}
          disabled={isELVCentre && candidatePastJobsCount === 0 ? !allTermsAccepted : false}
        >
          <RoundedTick color={isELVCentre && candidatePastJobsCount === 0 && !allTermsAccepted ? "#a0a0a0" : "black"}/>
          {isELVCentre && candidatePastJobsCount === 0 ? "Confirm" : "Yes, I Accept"}
        </CustomButton>
      </div>
      {/* Add your modal content here */}
    </PopupModal>
  );
};

export default AcceptJobModal;
